import React, { useMemo, useState } from 'react';

import { LinkProps } from '@soluto-design/link';
import { TitleProps } from '@soluto-design/react';
import cls from '@soluto-design/styles/cls';

import AccordionViewMore from '../../molecules/AccordionViewMore';
import BlockTitleButton, {
    BlockTitleButtonProps,
} from '../../molecules/BlockTitleButton';
import BlockTitleFilters, {
    BlockTitleFiltersProps,
} from '../../molecules/BlockTitleFilters';
import BlockTitleText, {
    BlockTitleTextProps,
} from '../../molecules/BlockTitleText';
import DynamicCard, { DynamicCardProps } from '../../molecules/DynamicCard';
import BlockTitleSmall, {
    BlockTitleSmallProps,
} from '../../molecules/BlockTitleSmall';

type DynamicCardWithFilter = DynamicCardProps & {
    filter?: string;
};

export interface GridSectionProps {
    title?: string;
    titleTag?: TitleProps['component'];
    text?: string;
    cta?: LinkProps;
    items: DynamicCardWithFilter[];
    variant:
        | 'grid-navigation-1'
        | 'grid-navigation-2'
        | 'grid-navigation-3'
        | 'grid-media-1'
        | 'grid-info-1'
        | 'grid-info-2'
        | 'grid-info-3'
        | 'grid-content-1'
        | 'grid-pricing-1'
        | 'cards-wrapper';
    viewMore?: {
        href?: string;
        label?: string;
        onClick?: () => void;
    };
    pagination?: {
        initialItems: number;
        itemsPerPage: number;
        page: number;
    };
    filters?: {
        name: string;
        items: DynamicCardWithFilter[];
    }[];
    filterActive?: string;
    className?: string;
}

const variantToCardType: Record<
    GridSectionProps['variant'],
    DynamicCardProps['cardType']
> = {
    'grid-navigation-1': 'navigationMedia',
    'grid-navigation-2': 'navigationMedia',
    'grid-navigation-3': 'navigationText',
    'grid-media-1': 'mediaSmall',
    'grid-info-1': 'value',
    'grid-info-2': 'iconLarge',
    'grid-info-3': 'iconSmall',
    'grid-content-1': 'content',
    'grid-pricing-1': 'pricing',
    'cards-wrapper': 'mediaLarge',
};

const gridByVariant: Record<GridSectionProps['variant'], string> = {
    'grid-navigation-1': 'md:col-span-6 xl:col-span-4',
    'grid-navigation-2': 'md:col-span-6 xl:col-span-6 hd:col-span-3',
    'grid-navigation-3': 'md:col-span-4',
    'grid-media-1': 'md:col-span-6 lg:col-span-4',
    'grid-info-1': 'md:col-span-6 lg:col-span-3',
    'grid-info-2': 'md:col-span-6 lg:col-span-4',
    'grid-info-3': 'md:col-span-full lg:col-span-6',
    'grid-content-1': 'md:col-span-6 lg:col-span-4',
    'grid-pricing-1': 'lg:col-span-4',
    'cards-wrapper': '',
};

type BlockTitleByPropsButton = {
    kind: 'button';
} & BlockTitleButtonProps;

type BlockTitleByPropsText = {
    kind: 'text';
} & BlockTitleTextProps;
type BlockTitleByPropsFilters = {
    kind: 'filters';
} & BlockTitleFiltersProps;

type BlockTitleByPropsSmall = {
    kind: 'small';
} & BlockTitleSmallProps;

type BlockTitleByPropsArgs =
    | BlockTitleByPropsText
    | BlockTitleByPropsButton
    | BlockTitleByPropsFilters
    | BlockTitleByPropsSmall;

const BlockTitleByProps = ({ kind, ...props }: BlockTitleByPropsArgs) => {
    switch (kind) {
        case 'button':
            return <BlockTitleButton {...(props as BlockTitleButtonProps)} />;
        case 'text':
            return <BlockTitleText {...(props as BlockTitleTextProps)} />;
        case 'small':
            return (
                <div className="mb-xl">
                    <BlockTitleSmall {...(props as BlockTitleByPropsSmall)} />
                </div>
            );
        case 'filters':
            return <BlockTitleFilters {...(props as BlockTitleFiltersProps)} />;
        default:
            return null;
    }
};

export default function GridSection({
    variant,
    title,
    titleTag = 'h3',
    text,
    cta,
    items = [],
    viewMore,
    pagination,
    filters = [],
    filterActive,
    className,
}: GridSectionProps) {
    const [filterSelected, setFilterSelected] = useState<string | null>(
        filterActive ?? filters?.length ? filters[0].name : null,
    );

    const cardType = variantToCardType?.[variant];

    const thisItems = useMemo(() => {
        if (filters?.length) {
            const filtersItems = filters.reduce((acc, filter) => {
                if (!filter?.items || !filter?.items.length) {
                    return acc;
                }

                let filterItems = filter.items.map((item) => {
                    return {
                        ...item,
                        filter: filter.name,
                    };
                });

                acc.push(...filterItems);

                return acc;
            }, [] as DynamicCardWithFilter[]);

            return filtersItems;
        }

        return items;
    }, [items, filters]);

    const itemsVisible = useMemo(
        () =>
            pagination
                ? pagination?.itemsPerPage * (pagination?.page - 1) +
                  pagination.initialItems
                : thisItems.length,
        [pagination, thisItems],
    );

    const showViewMore = useMemo(
        () =>
            pagination ? itemsVisible < thisItems?.length ?? false : !!viewMore,
        [itemsVisible, pagination, thisItems, viewMore],
    );

    const handleFilterClick = (filterName: string) => {
        setFilterSelected((prevFilter) => {
            if (prevFilter === filterName) {
                return null;
            }

            return filterName;
        });
    };

    const kindTitleProps: BlockTitleByPropsArgs = useMemo(() => {
        if (!!filters?.length) {
            return {
                kind: 'filters',
                title,
                text,
                titleTag,
                filters: filters?.map((filter) => filter.name),
                filterSelected,
                onClickFilter: handleFilterClick,
            };
        }

        if (text && cta) {
            return {
                kind: 'small',
                title,
                text,
                cta,
                titleTag,
            };
        }

        if (cta) {
            return {
                kind: 'button',
                title,
                button: cta,
                titleTag,
            };
        }

        return {
            kind: 'text',
            title,
            text,
            titleTag,
        };
    }, [title, text, cta, titleTag, filters, filterSelected]);

    return (
        <section className={cls('my-xxxl', className)}>
            <div className="container">
                <BlockTitleByProps {...kindTitleProps} />
                <div className="row md:grid-cols-12 gap-y-gutter items-stretch">
                    {!!thisItems?.length && (
                        <>
                            {thisItems?.map((item, realIndex) => {
                                const isLast = pagination
                                    ? itemsVisible === thisItems.length
                                    : realIndex === thisItems.length - 1;
                                const isFilterSelected = !!filters?.length
                                    ? !filterSelected ||
                                      filterSelected === item?.filter
                                    : true;

                                const detachedItem =
                                    variant === 'grid-info-3' &&
                                    isLast &&
                                    itemsVisible % 2 !== 0;

                                const customCardType =
                                    item?.cardType ?? cardType;

                                return (
                                    <div
                                        key={`grid-section-item${variant}-${cardType}-${realIndex}`}
                                        className={cls(
                                            'col-span-full [&>*]:h-full',
                                            !detachedItem &&
                                                gridByVariant[variant],
                                            realIndex >= itemsVisible ||
                                                !isFilterSelected
                                                ? 'hidden'
                                                : cls(
                                                      item.showOnlyXs &&
                                                          'md:hidden',
                                                      item.showOnLg &&
                                                          'hidden lg:block',
                                                  ),
                                        )}>
                                        <DynamicCard
                                            {...item}
                                            cardType={customCardType}
                                        />
                                    </div>
                                );
                            })}
                        </>
                    )}
                </div>
                {showViewMore && (
                    <div className="mt-xl">
                        <AccordionViewMore
                            label={viewMore?.label as string}
                            onClick={viewMore?.onClick}
                            href={viewMore?.href}
                        />
                    </div>
                )}
            </div>
        </section>
    );
}
